import React, { useMemo, useState } from 'react';
import { useAdminDivisions } from '../../hooks/useAdminDivisions.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const DivisionsSection = ({ onNotify }) => {
  const { items, loading, error, refresh, saveDivision, deleteDivision } = useAdminDivisions();
  const [search, setSearch] = useState('');
  const [editorState, setEditorState] = useState({ open: false, record: null, saving: false });

  useLucideIcons([items.length, search, editorState.open]);

  const filteredItems = useMemo(() => {
    if (!search) {
      return items;
    }
    const query = search.toLowerCase();
    return items.filter((division) => {
      const name = division.nombre?.toLowerCase() || '';
      const boh = division.boh?.toString() || '';
      return name.includes(query) || boh.includes(query);
    });
  }, [items, search]);

  const handleOpenModal = (record = null) => {
    setEditorState({
      open: true,
      saving: false,
      record: record
        ? { ...record }
        : { nombre: '', boh: '', activo: true }
    });
  };

  const handleSave = async (values) => {
    setEditorState((prev) => ({ ...prev, saving: true }));
    try {
      await saveDivision(values);
      setEditorState({ open: false, record: null, saving: false });
      refresh();
      onNotify?.({
        type: 'success',
        message: values?.id ? 'División actualizada correctamente.' : 'División creada correctamente.'
      });
    } catch (err) {
      setEditorState((prev) => ({ ...prev, saving: false }));
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo guardar la división.' });
    }
  };

  const handleDeactivate = async (record) => {
    if (!record.activo) {
      return;
    }
    const confirmed = window.confirm(`¿Desactivar la división "${record.nombre}"?`);
    if (!confirmed) {
      return;
    }
    try {
      await deleteDivision(record.id);
      refresh();
      onNotify?.({ type: 'success', message: 'División desactivada.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo desactivar la división.' });
    }
  };

  const closeModal = () => {
    setEditorState({ open: false, record: null, saving: false });
  };

  return (
    <section id="divisions-section" className="admin-section active">
      <div className="section-header">
        <h2 className="section-title">Divisiones</h2>
        <div className="section-actions">
          <div className="search-container">
            <span className="search-icon"><i data-lucide="search" /></span>
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por nombre o BoH..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search ? (
              <button type="button" className="btn-secondary" onClick={() => setSearch('')}>
                Limpiar
              </button>
            ) : null}
          </div>
          <button className="btn-primary" type="button" onClick={() => handleOpenModal()}>
            <span className="btn-icon"><i data-lucide="plus" /></span>
            Nueva División
          </button>
        </div>
      </div>
      <div className="data-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Cargando divisiones...</div>
          </div>
        ) : (
          <table className="data-table" id="divisions-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>BoH</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={4} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                    <div className="empty-state-text">{error}</div>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={4} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="layout-grid" /></div>
                    <div className="empty-state-text">No hay divisiones configuradas.</div>
                    <div className="empty-state-subtext">Crea tu primera división para comenzar.</div>
                  </td>
                </tr>
              ) : (
                filteredItems.map((division) => (
                  <tr key={division.id}>
                    <td>{division.nombre}</td>
                    <td>{division.boh}</td>
                    <td>
                      <span className={`status-badge ${division.activo ? 'status-active' : 'status-inactive'}`}>
                        {division.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn edit" type="button" onClick={() => handleOpenModal(division)}>
                          <i data-lucide="edit" />
                        </button>
                        <button className="action-btn delete" type="button" onClick={() => handleDeactivate(division)}>
                          <i data-lucide="circle-off" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {editorState.open ? (
        <DivisionModal state={editorState} onClose={closeModal} onSubmit={handleSave} />
      ) : null}
    </section>
  );
};

const DivisionModal = ({ state, onClose, onSubmit }) => {
  const [formValues, setFormValues] = useState(() => ({
    ...state.record,
    nombre: state.record?.nombre || '',
    boh: state.record?.boh ?? '',
    activo: state.record?.activo ?? true
  }));

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({ ...formValues, id: state.record?.id || null });
  };

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content" style={{ maxWidth: '520px' }}>
        <div className="modal-header">
          <h3>{state.record?.id ? 'Editar División' : 'Nueva División'}</h3>
          <button className="modal-close" type="button" onClick={onClose}>
            <i data-lucide="x" />
          </button>
        </div>
        <form className="modal-body form-grid" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Nombre</label>
            <input
              type="text"
              className="form-input"
              name="nombre"
              value={formValues.nombre}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">BoH</label>
            <input
              type="number"
              className="form-input"
              name="boh"
              value={formValues.boh}
              onChange={handleChange}
              min="0"
              step="1"
              required
            />
          </div>
          <div className="form-group checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="activo"
                checked={formValues.activo}
                onChange={handleChange}
              />
              <span className="checkmark" />
              <span className="checkbox-label">División activa</span>
            </label>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" type="button" onClick={onClose} disabled={state.saving}>
              Cancelar
            </button>
            <button className="btn-primary" type="submit" disabled={state.saving}>
              {state.saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DivisionsSection;
